import "moment/locale/fr";
import "moment/locale/ar";
import moment from "moment";
import momentTZ from "moment-timezone";

const IsoDateFormat = "yyyy-MM-DD";
const monthYearFormat = "MMM yyyy";
const IsoDateTimeFormat = "yyyy-MM-DDTHH:mm:ss";
// const IsoDateTimeFormat = "yyyy-MM-DDThh:mm:ss.SSS"; // with milliseconds
// const IsoDateTimeFormat = "yyyy-MM-DDThh:mm:ssZ"; // with timezone
const DefaultDateFormat = "DD MMM yyyy";
const DefaultTimeFormat = "hh:mm";
const DefaultDateTimeFormat = "DD MMM yyyy HH:mm";
const DateTimeFormat24 = "DD MMM yyyy HH:mm";
const DateTimeFormat24withSeconds = "DDMMMyyyy HH.mm.ss";

export const applyUsersTimezone = (serviceDate) => {
  //this function will always return the date that the service returned, but it will make it appear as it is in the users timezone
  //It will convert the service date to the user's timezone, without changing value.
  if (!serviceDate) {
    return "";
  }

  let dt = new Date(serviceDate);

  if (!dt) {
    return "";
  }

  //Offset Positive values mean that the local time is behind UTC, and negative values mean that the local time is ahead of UTC.
  let offset = new Date(serviceDate).getTimezoneOffset();

  let newDate = new Date(serviceDate);
  newDate.setMinutes(dt.getMinutes() + offset);

  //console.log({
  //  serviceDate: serviceDate,
  //  offset: offset,
  //  newDate: newDate,
  //});

  return newDate;
};

export const addYears = (date, years) => {
  date?.setFullYear(date?.getFullYear() + years);

  return date;
};

export const setDateTimeLanguage = (language) => {
  moment.locale(language);
};

export const formatDateTimeFromNow = (dateTime) => {
  //console.log("formatDateTimeFromNow", moment(dateTime));
  return moment(dateTime).fromNow();
};

export const formatLddNoTimezone = (date) => {
  if (!date) {
    return "";
  }
  return moment(date).format("DD MMM yyyy");
};

export const formatIsoDate = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(IsoDateFormat);
};

export const formatIsoDateWithoutLocale = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).locale("en").format(IsoDateFormat);
};

export const formatIsoDateTime = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(IsoDateTimeFormat);
};

export const formatDate = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(DefaultDateFormat);
};

export const formatDateWithoutLocale = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).locale("en").format(DefaultDateFormat);
};

export const formatDate24 = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(DateTimeFormat24);
};

//Returns the user's browser time (08 Jun 2022 09:52:36)
export const formatDate24withSeconds = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(DateTimeFormat24withSeconds);
};

export const formatTime = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(DefaultTimeFormat);
};

export const formatDateTime = (date) => {
  if (!date) {
    return "";
  }
  if (!moment.isDate(date)) {
    return "";
  }
  return moment(date).format(DefaultDateTimeFormat);
};

export const formatDateIsoString = (date) => {
  if (!date) {
    return "";
  }
  return moment(parseIsoDate(date)).format(DefaultDateFormat);
};

export const formatDateTimeIsoString = (date) => {
  if (!date) {
    return "";
  }
  return moment(parseIsoDateTime(date)).format(DefaultDateTimeFormat);
};

export const formatDateMonthYear = (date) => {
  if (!date) {
    return "";
  }
  return moment(parseIsoDateTime(date)).format(monthYearFormat);
};

export const formatToIsoDateFormat = (date) => {
  if (!date) {
    return "";
  }
  return moment(parseIsoDateTime(date)).format(IsoDateFormat);
};

export const formatToExcelDate = (isoDateString, timezone) => {
  return moment.tz(isoDateString, timezone).toDate();
};

export const parseIsoDate = (date) => {
  if (!date) {
    throw new Error("Invalid ISO date!");
  }
  return moment(date, IsoDateFormat).toDate();
};

export const parseIsoDateTime = (dateTime) => {
  if (!dateTime) {
    throw new Error("Invalid ISO datetime!");
  }

  return moment(dateTime, IsoDateTimeFormat).toDate();
};

export const parseDate = (date) => {
  if (!date) {
    throw new Error("Invalid date!");
  }
  return moment(date, DefaultDateFormat).toDate();
};

export const parseDateTime = (dateTime) => {
  if (!dateTime) {
    throw new Error("Invalid datetime!");
  }
  return moment(dateTime, DefaultDateTimeFormat).toDate();
};

export const getDuration = (startDate, endDate) => {
  if (!moment.isMoment(startDate))
    throw new Error(
      `Start date must be a moment object, received ${typeof startDate}`
    );
  if (endDate && !moment.isMoment(endDate))
    throw new Error(
      `End date must be a moment object, received ${typeof startDate}`
    );

  return `${startDate.format("ll")} - ${
    endDate ? endDate.format("ll") : "Present"
  } • ${startDate.from(endDate || moment(), true)}`;
};

export const getDuration1 = (startDate, endDate) => {
  if (!moment.isMoment(startDate))
    throw new Error(
      `Start date must be a moment object, received ${typeof startDate}`
    );
  if (endDate && !moment.isMoment(endDate))
    throw new Error(
      `End date must be a moment object, received ${typeof startDate}`
    );

  return `${startDate.from(endDate || moment(), true)}`;
};

export const getTimezones = () => {
  return momentTZ.tz.names();
};

export const getLocalTimezone = () => {
  return momentTZ.tz.guess();
};

export const today = () => {
  return moment().startOf("day").valueOf();
};

export const startOfWeek = (weekDiff) => {
  weekDiff = -(weekDiff || 0);
  return moment().startOf("week").subtract(weekDiff, "week").toDate();
};

export const endOfWeek = (weekDiff) => {
  weekDiff = -(weekDiff || 0);
  return moment()
    .startOf("week")
    .subtract(weekDiff, "week")
    .add(6, "day")
    .toDate();
};

export const startOfMonth = (monthDiff) => {
  monthDiff = -(monthDiff || 0);
  return moment().startOf("month").subtract(monthDiff, "month").toDate();
};

export const endOfMonth = (monthDiff) => {
  monthDiff = -(monthDiff || 0);
  return moment().subtract(monthDiff, "month").endOf("month").toDate();
  // return moment()
  //   .startOf("month")
  //   .subtract(monthDiff, "month")
  //   .add(1, "month")
  //   .subtract(1, "day")
  //   .toDate();
};

export const startOfYear = (yearDiff) => {
  yearDiff = -(yearDiff || 0);
  return moment().startOf("year").subtract(yearDiff, "year").toDate();
};

export const startOfCurrentYear = () => {
  return new Date(new Date().getFullYear(), 0, 1);
};

export const endOfCurrentYear = () => {
  return new Date(new Date().getFullYear(), 11, 31);
};

export const past7DaysStartDate = (weekDiff) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  return start;
};

export const past30DaysStartDate = (weekDiff) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return start;
};

export const past6MonthsStartDate = (weekDiff) => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 6, end.getDate());
  return start;
};

export const formatTimeLocal = (date, timezone) => {
  return formatTime(parseIsoDateTime(momentTZ.tz(date, timezone).format()));
};

export const formatDateLocal = (date, timezone) => {
  return formatDate(parseIsoDateTime(momentTZ.tz(date, timezone).format()));
};

export const formatDateTimeLocal = (date, timezone) => {
  // Returns 27 Oct 2017 23:59
  return formatDate24(parseIsoDateTime(momentTZ.tz(date, timezone).format()));
};

export const formatDateTimeExcel = (date, timezone) => {
  return parseIsoDateTime(momentTZ.tz(date, timezone).format());
};

export const formatTimezone = (date, timezone) => {
  if (!timezone) {
    return moment(date).format();
  }
  return moment(date).tz(timezone).format();
};

export const formatTimezoneISO = (date, timezone) => {
  if (!timezone) {
    return moment(date).format();
  }
  return moment.utc(date).tz(timezone).format(IsoDateTimeFormat);
};

export const formatTimezoneDateTime = (date, timezone) => {
  if (!timezone) {
    return moment(date).format();
  }
  return moment.utc(date).tz(timezone).format(DefaultDateTimeFormat);
};
export const formatTimezoneDate = (date, timezone) => {
  if (!timezone) {
    return moment(date).format();
  }
  return moment.utc(date).tz(timezone).format(DefaultDateFormat);
};
export const formatTimezoneTime = (date, timezone) => {
  if (!timezone) {
    return moment(date).format();
  }
  return moment.utc(date).tz(timezone).format(DefaultTimeFormat);
};

export const daysAgo = (date, timezone, t) => {
  return t("common:daysAgo", {
    daysAgo: moment().diff(formatTimezone(date, timezone), "days"),
  });
};

export const getFormattedDateFromNow = (occurredAt, timezone, t) => {
  if (isMoreThan2Days(occurredAt, timezone)) {
    return daysAgo(occurredAt, timezone, t);
  } else {
    return formatDateTimeFromNow(
      parseIsoDateTime(formatTimezone(occurredAt, timezone))
    );
  }
};

export const isMoreThan2Days = (occurredAt, timezone) => {
  const hours =
    Math.abs(
      parseIsoDateTime(formatTimezone(occurredAt, timezone)) - new Date()
    ) / 36e5;

  if (hours >= 48) {
    return true;
  }
  return false;
};

export const isValidDate = (date) => {
  if (!moment.isDate(date)) {
    return false;
  }
  return true;
};

export const toMomentDate = (date) => {
  return moment(date);
};

/**
 * Convert an epoch date (Unix date time) to Date object
 * @param epoch
 * @return {Date}
 */
export const parseEpochDate = (epoch) => {
  const date = new Date(0);
  date.setUTCSeconds(epoch);
  return date;
};

/**
 * Check if a Date is in the past
 * @param {Date} date
 * @return {boolean}
 */
export const isPastDate = (date) => {
  const now = new Date().getTime();
  return date < now;
};

export const getFormattedDateRange = (dates) => {
  if (!dates?.from) {
    return "";
  }

  const formattedFromDate = formatDate(dates.from);
  const formattedToDate = dates.to ? ` - ${formatDate(dates.to)}` : "";

  return `${formattedFromDate}${formattedToDate}`;
};
